// Grafana Faro Configuration and Enhanced Business Tracking
// This module provides comprehensive frontend observability with business context

class FaroBusinessTracker {
  constructor() {
    this.sessionStart = Date.now();
    this.userJourney = [];
    this.cartSessionId = null;
    this.currentPage = null;
  }

  // Initialize business tracking when Faro is ready
  initialize() {
    if (typeof window.GrafanaFaroWebSdk === 'undefined') {
      console.warn('Faro SDK not loaded, retrying in 1s...');
      setTimeout(() => this.initialize(), 1000);
      return;
    }

    this.faro = window.GrafanaFaroWebSdk.faro;
    console.log('✅ Faro Business Tracker initialized');
    
    // Track initial page load
    this.trackPageView(window.location.pathname);
    
    // Set up performance monitoring
    this.setupPerformanceMonitoring();
    
    // Track session start
    this.trackEvent('session_start', {
      session_start_time: this.sessionStart,
      user_agent: navigator.userAgent,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      referrer: document.referrer || 'direct'
    });
  }

  // Track business events with context (fire-and-forget to never block)
  trackEvent(eventName, properties = {}) {
    if (!this.faro) return;

    // Use setTimeout(0) to make this truly async and non-blocking
    setTimeout(() => {
      try {
        const enrichedProperties = {
          ...properties,
          session_duration: Date.now() - this.sessionStart,
          page: this.currentPage,
          cart_session_id: this.cartSessionId,
          user_journey_step: this.userJourney.length,
          timestamp: new Date().toISOString()
        };

        this.faro.api.pushEvent(eventName, enrichedProperties);
        console.log(`📊 Faro Event: ${eventName}`, enrichedProperties);
      } catch (error) {
        // Silently fail - tracking should never block user operations
        console.warn('Faro event tracking failed:', error);
      }
    }, 0);
  }

  // Enhanced page view tracking
  trackPageView(page) {
    this.currentPage = page;
    this.userJourney.push({
      page,
      timestamp: Date.now(),
      session_time: Date.now() - this.sessionStart
    });

    this.trackEvent('page_view', {
      page,
      journey_step: this.userJourney.length,
      previous_page: this.userJourney.length > 1 ? this.userJourney[this.userJourney.length - 2].page : null
    });
  }

  // Track API calls with comprehensive context
  trackApiCall(method, url, status, duration, requestSize, responseSize, error = null) {
    if (!this.faro) return;

    try {
      // Track HTTP error events
      if (error) {
        this.trackEvent('http_error', {
          method,
          url: this.sanitizeUrl(url),
          status,
          error_message: error.message,
          error_type: error.name,
          duration,
          'http.method': method,
          'http.status_code': status,
          'http.duration': duration,
          'page.current': this.currentPage,
          'session.id': this.cartSessionId
        });
      }

      // Track HTTP insights metrics  
      this.trackEvent('http_request', {
        method,
        url: this.sanitizeUrl(url),
        status,
        duration,
        request_size: requestSize || 0,
        response_size: responseSize || 0,
        ttfb: duration, // For API calls, duration ≈ TTFB
        success: status >= 200 && status < 400,
        'http.method': method,
        'http.status_code': status,
        'http.duration': duration,
        'page.current': this.currentPage,
        'session.id': this.cartSessionId,
        'user.journey_step': this.userJourney.length
      });
    } catch (faroError) {
      console.warn('Faro HTTP tracking failed:', faroError);
    }
  }

  // Track business conversion funnel
  trackConversionStep(step, properties = {}) {
    const conversionSteps = ['homepage_visit', 'product_view', 'add_to_cart', 'cart_view', 'checkout_start', 'payment_submit', 'order_complete'];
    const stepIndex = conversionSteps.indexOf(step);

    this.trackEvent('conversion_funnel', {
      step,
      step_index: stepIndex,
      step_name: step,
      ...properties,
      funnel_completion_rate: stepIndex >= 0 ? (stepIndex + 1) / conversionSteps.length : 0
    });
  }

  // Track cart operations with detailed context (fire-and-forget)
  trackCartOperation(operation, productId, quantity, cartTotal, cartItemCount) {
    // Use setTimeout(0) to make this truly async and non-blocking
    setTimeout(() => {
      try {
        this.trackEvent('cart_operation', {
          operation, // 'add', 'remove', 'update', 'clear'
          product_id: productId,
          quantity,
          cart_total_value: cartTotal,
          cart_item_count: cartItemCount,
          session_id: this.cartSessionId
        });

        // Track conversion funnel
        if (operation === 'add') {
          this.trackConversionStep('add_to_cart', {
            product_id: productId,
            quantity
          });
        }
      } catch (error) {
        // Silently fail - tracking should never block user operations
        console.warn('Faro cart operation tracking failed:', error);
      }
    }, 0);
  }

  // Track payment attempts with detailed context
  trackPaymentAttempt(paymentData, result, error = null) {
    const baseData = {
      payment_method: 'card',
      total_amount: paymentData.total || 0,
      items_count: paymentData.items?.length || 0,
      session_id: this.cartSessionId
    };

    if (result === 'success') {
      this.trackEvent('payment_success', baseData);
      this.trackConversionStep('order_complete', baseData);
    } else {
      this.trackEvent('payment_failure', {
        ...baseData,
        failure_reason: error?.message || 'unknown',
        failure_type: this.categorizePaymentError(error)
      });
    }
  }

  // Track performance metrics
  trackPerformanceMetric(metric, value, context = {}) {
    this.trackEvent('performance_metric', {
      metric,
      value,
      ...context,
      page: this.currentPage
    });
  }

  // Setup performance monitoring
  setupPerformanceMonitoring() {
    // Monitor Core Web Vitals
    if ('PerformanceObserver' in window) {
      try {
        // Largest Contentful Paint
        new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1];
          this.trackPerformanceMetric('lcp', lastEntry.renderTime || lastEntry.loadTime);
        }).observe({ entryTypes: ['largest-contentful-paint'] });

        // First Input Delay
        new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach(entry => {
            this.trackPerformanceMetric('fid', entry.processingStart - entry.startTime);
          });
        }).observe({ entryTypes: ['first-input'] });

        // Cumulative Layout Shift
        new PerformanceObserver((list) => {
          let clsValue = 0;
          const entries = list.getEntries();
          entries.forEach(entry => {
            if (!entry.hadRecentInput) {
              clsValue += entry.value;
            }
          });
          this.trackPerformanceMetric('cls', clsValue);
        }).observe({ entryTypes: ['layout-shift'] });

      } catch (error) {
        console.warn('Performance monitoring setup failed:', error);
      }
    }

    // Track resource loading performance
    window.addEventListener('load', () => {
      const navTiming = performance.getEntriesByType('navigation')[0];
      if (navTiming) {
        this.trackPerformanceMetric('page_load_time', navTiming.loadEventEnd - navTiming.fetchStart);
        this.trackPerformanceMetric('dom_content_loaded', navTiming.domContentLoadedEventEnd - navTiming.fetchStart);
        this.trackPerformanceMetric('ttfb', navTiming.responseStart - navTiming.requestStart);
      }
    });
  }

  // Enhanced error tracking (fire-and-forget to never block)
  trackError(error, context = {}) {
    if (!this.faro) return;

    // Use setTimeout(0) to make this truly async and non-blocking
    setTimeout(() => {
      try {
        this.faro.api.pushError(error, {
          ...context,
          page: this.currentPage,
          session_id: this.cartSessionId,
          user_journey: this.userJourney.slice(-3), // Last 3 pages
          session_duration: Date.now() - this.sessionStart
        });

        this.trackEvent('frontend_error', {
          error_name: error.name,
          error_message: error.message,
          error_stack: error.stack?.substring(0, 500), // Truncate for size
          ...context
        });
      } catch (trackingError) {
        // Silently fail - tracking should never block user operations
        console.warn('Faro error tracking failed:', trackingError);
      }
    }, 0);
  }

  // Utility methods
  sanitizeUrl(url) {
    // Remove sensitive data from URLs for logging
    return url.replace(/session_id=[^&]+/g, 'session_id=***');
  }

  categorizePaymentError(error) {
    if (!error) return 'unknown';
    
    const message = error.message?.toLowerCase() || '';
    if (message.includes('fraud')) return 'fraud_detection';
    if (message.includes('invalid card')) return 'card_validation';
    if (message.includes('insufficient')) return 'insufficient_funds';
    if (message.includes('timeout')) return 'gateway_timeout';
    if (message.includes('network')) return 'network_error';
    return 'other';
  }

  // Session management
  setCartSessionId(sessionId) {
    this.cartSessionId = sessionId;
    this.trackEvent('session_identified', { session_id: sessionId });
  }

  // Journey tracking helpers
  getCurrentJourneyState() {
    return {
      current_page: this.currentPage,
      journey_length: this.userJourney.length,
      session_duration: Date.now() - this.sessionStart,
      cart_session_id: this.cartSessionId
    };
  }

  // Add startUserAction method to delegate to Faro API
  startUserAction(name, attributes = {}, options = {}) {
    // Call Faro API directly if available
    const faro = window.GrafanaFaroWebSdk?.faro;
    if (faro && faro.api && faro.api.startUserAction) {
      try {
        console.log('🎯 Starting user action:', name, attributes);
        const userAction = faro.api.startUserAction(name, attributes, options);
        console.log('✅ User action created:', name, userAction);
        return userAction;
      } catch (e) {
        console.error('❌ Faro user action tracking error:', e);
      }
    } else {
      console.warn('⚠️ User Actions API not available. Faro SDK:', !!faro, 'API:', !!faro?.api);
    }
  }
}

// Create global instance
// Check if window.faroTracker already exists (from index.html wrapper)
if (window.faroTracker && typeof window.faroTracker.startUserAction === 'function') {
  // Merge: keep the wrapper's startUserAction but add all methods from FaroBusinessTracker
  const wrapper = window.faroTracker;
  const instance = new FaroBusinessTracker();
  // Copy all methods from instance to wrapper, but preserve startUserAction from wrapper
  Object.keys(instance).forEach(key => {
    if (key !== 'startUserAction' && typeof instance[key] === 'function') {
      wrapper[key] = instance[key].bind(instance);
    }
  });
  // Copy non-function properties
  Object.keys(instance).forEach(key => {
    if (typeof instance[key] !== 'function') {
      wrapper[key] = instance[key];
    }
  });
  // Initialize the merged tracker
  instance.initialize();
} else {
  // Otherwise, just use the instance
  window.faroTracker = new FaroBusinessTracker();
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    setTimeout(() => window.faroTracker.initialize(), 2000); // Wait for Faro to load
  });
} else {
  setTimeout(() => window.faroTracker.initialize(), 2000);
}

export default window.faroTracker;
